/**
 * @fileoverview Directory option API service.
 * @description The one place the option lists several features offer are fetched: the roles the
 * signed-in admin may assign (GET /authorization/me/assignable-roles?purpose=INVITATION), the
 * reporting managers they may choose for a role (GET /users/eligible-reporting-managers) and the
 * prospect metadata catalogues, where the State choices come from (GET /tenants/prospect-metadata).
 * Add User, Invite User and Reactivate User offer the same roles and managers, and the Sales
 * pipeline and the Add and Edit User forms read the same catalogues, so each feature calls these
 * (and shares their query keys) instead of re-implementing the requests.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { httpClient } from '@/shared/api/http-client'

import type { AssignableRole, ReportingManagerOption } from '@/shared/types/directory-options.types'
import type { ProspectMetadata } from '@/shared/types/prospect-metadata.types'

// The option lists are small; one page of the maximum size covers them.
export const OPTION_LIST_LIMIT = 100

/**
 * @description Fetches the roles the signed-in admin may put on an invitation.
 * @returns {Promise<AssignableRole[]>} The assignable roles.
 */
export async function getAssignableRoles(): Promise<AssignableRole[]> {
  const { data } = await httpClient.get<AssignableRole[]>('/authorization/me/assignable-roles', {
    params: { purpose: 'INVITATION', limit: OPTION_LIST_LIMIT },
  })
  return data
}

/**
 * @description Fetches who a user with the given role may report to (Sales roles only).
 * @param {string} targetRoleId - The role the user will have.
 * @returns {Promise<ReportingManagerOption[]>} The eligible managers.
 */
export async function getEligibleReportingManagers(
  targetRoleId: string,
): Promise<ReportingManagerOption[]> {
  const { data } = await httpClient.get<ReportingManagerOption[]>(
    '/users/eligible-reporting-managers',
    { params: { targetRoleId, limit: OPTION_LIST_LIMIT } },
  )
  return data
}

/**
 * @description Fetches the prospect metadata catalogues.
 * @returns {Promise<ProspectMetadata>} Every catalogue and the tier scheme.
 */
export async function getProspectMetadata(): Promise<ProspectMetadata> {
  const { data } = await httpClient.get<ProspectMetadata>('/tenants/prospect-metadata')
  return data
}
